import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { flushOfflineQueue, isOnline, pendingCount } from "../lib/offlineCart";
import { toast } from "sonner";

/**
 * PWAContext — wires up:
 *  - Service worker registration (only in production builds)
 *  - online/offline tracking exposed to consumers
 *  - Offline-cart queue drain on `online` event + boot
 *  - beforeinstallprompt capture for the install banner
 */

const PWAContext = createContext({
  online: true,
  pendingOps: 0,
  installPromptEvent: null,
  promptInstall: async () => {},
  isInstallable: false,
});

export function PWAProvider({ children }) {
  const [online, setOnline] = useState(isOnline());
  const [pendingOps, setPendingOps] = useState(0);
  const [installEvt, setInstallEvt] = useState(null);

  const refreshPending = useCallback(async () => {
    try { setPendingOps(await pendingCount()); } catch { /* ignore */ }
  }, []);

  // Register service worker (production only — avoids the dev-mode hot-reload issue)
  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
    if (window.location.hostname === "localhost" || window.location.hostname.startsWith("127.")) return;

    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .catch(() => { /* network down on first load — fine */ });
  }, []);

  // Online/offline listeners
  useEffect(() => {
    const goOnline = async () => {
      setOnline(true);
      const synced = await flushOfflineQueue();
      await refreshPending();
      if (synced > 0) {
        toast.success(`Back online — synced ${synced} cart change${synced > 1 ? "s" : ""}`);
      }
    };
    const goOffline = () => {
      setOnline(false);
      toast.message("You're offline — changes will sync when you're back");
    };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    refreshPending();
    // First boot: if we have a queue + we're online, drain it immediately
    if (isOnline()) {
      flushOfflineQueue().then((synced) => {
        if (synced > 0) {
          toast.success(`Synced ${synced} pending cart change${synced > 1 ? "s" : ""}`);
          refreshPending();
        }
      });
    }
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [refreshPending]);

  // Install prompt capture
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallEvt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installEvt) return null;
    installEvt.prompt();
    const { outcome } = await installEvt.userChoice;
    setInstallEvt(null);
    return outcome;
  }, [installEvt]);

  return (
    <PWAContext.Provider
      value={{
        online,
        pendingOps,
        refreshPending,
        installPromptEvent: installEvt,
        isInstallable: Boolean(installEvt),
        promptInstall,
      }}
    >
      {children}
    </PWAContext.Provider>
  );
}

export const usePWA = () => useContext(PWAContext);
